import type { MemberTutorialKey, MemberTutorialState } from '@mlm/shared';
import type { OnboardingStage } from '../api';
import type { TutorialStep } from './steps';
import type { TutorialMode } from './store';

/**
 * The decisions behind the tutorials, as pure functions: the order of these
 * checks is what keeps a member from being interrupted at the wrong moment, and
 * this app's test setup has no DOM to exercise them through the components.
 */

/** A route ending in `/` matches anything under it; anything else is exact. */
export function routeMatches(route: string, pathname: string): boolean {
  return route.endsWith('/') ? pathname.startsWith(route) : pathname === route;
}

export const PAYMENT_ROUTES = ['/orders/', '/pay/'];

/**
 * Where the "your code is ready" prompt may appear. A member reading their
 * orders or their bell is free to be told; one in the middle of buying something
 * in the shop is not.
 */
export const ACTIVATE_START_ROUTES = [
  '/dashboard',
  '/orders',
  '/orders/',
  '/code-vault',
  '/notifications',
];

/**
 * The step to show: the first one in list order that belongs on this page, has
 * not been pressed past, and whose element is actually on screen. Because this
 * reads the page rather than counting steps, a reload, the back button and a
 * return from a payment provider all land on the right step by themselves.
 */
export function resolveStep(
  steps: TutorialStep[],
  pathname: string,
  seen: string[],
  isOnScreen: (target: string) => boolean,
): TutorialStep | null {
  for (const step of steps) {
    if (!step.routes.some((r) => routeMatches(r, pathname))) continue;
    if (seen.includes(step.id)) continue;
    if (step.target && !isOnScreen(step.target)) continue;
    return step;
  }
  return null;
}

export type TutorialSignals = {
  stage: OnboardingStage;
  activationCodeCount: number;
  needsPayment: boolean;
};

/** Practice whenever the real step is already behind them or isn't theirs to do. */
export function modeFor(
  key: MemberTutorialKey,
  signals: TutorialSignals,
  impersonating: boolean,
): TutorialMode {
  if (impersonating) return 'practice';
  if (key === 'kit') {
    const stillAhead =
      signals.stage === 'registered' ||
      (signals.stage === 'payment_pending' && signals.needsPayment);
    return stillAhead ? 'live' : 'practice';
  }
  return signals.stage === 'code_in_vault' && signals.activationCodeCount > 0 ? 'live' : 'practice';
}

export type AutoRunDecision =
  | { kind: 'none' }
  | { kind: 'start'; key: MemberTutorialKey }
  /** The member got past this one on their own, so it is no longer owed. */
  | { kind: 'close'; key: MemberTutorialKey };

/**
 * What to do without the member asking. Nothing at all for a member not in line
 * for the tutorials, or for an admin's Login-as session.
 */
export function decideAutoRun(input: {
  state: MemberTutorialState | null;
  signals: TutorialSignals;
  pathname: string;
  impersonating: boolean;
  activeKey: MemberTutorialKey | null;
  activeMode: TutorialMode | null;
  ended: MemberTutorialKey[];
  /** The tenant has kits on sale, so there is something to buy. */
  kitsAvailable: boolean;
}): AutoRunDecision {
  const { state, signals, pathname } = input;
  if (!state || input.impersonating) return { kind: 'none' };
  const liveKey = input.activeMode === 'live' ? input.activeKey : null;

  const pastKit =
    signals.stage === 'code_in_vault' ||
    signals.stage === 'activated' ||
    signals.stage === 'rolling';
  const pastActivate = signals.stage === 'activated' || signals.stage === 'rolling';

  // Past it on their own: a code came from a transfer or the till, a slot was
  // made for them, or the tab closed on the last screen. A proof already in also
  // counts, unless the running tutorial is about to say so itself.
  if (state.kit === null && !input.ended.includes('kit') && liveKey !== 'kit') {
    const proofAlreadyIn = signals.stage === 'payment_pending' && !signals.needsPayment;
    if (pastKit || proofAlreadyIn) return { kind: 'close', key: 'kit' };
  }
  if (liveKey === 'kit' && pastKit) return { kind: 'close', key: 'kit' };
  if (
    state.activate === null &&
    pastActivate &&
    !input.ended.includes('activate') &&
    liveKey !== 'activate'
  ) {
    return { kind: 'close', key: 'activate' };
  }

  if (input.activeKey) return { kind: 'none' };

  if (
    state.activate === null &&
    !input.ended.includes('activate') &&
    signals.stage === 'code_in_vault' &&
    signals.activationCodeCount > 0 &&
    ACTIVATE_START_ROUTES.some((r) => routeMatches(r, pathname))
  ) {
    return { kind: 'start', key: 'activate' };
  }

  if (state.kit === null && !input.ended.includes('kit') && input.kitsAvailable) {
    if (signals.stage === 'registered') {
      return pathname === '/dashboard' || pathname === '/checkout'
        ? { kind: 'start', key: 'kit' }
        : { kind: 'none' };
    }
    if (signals.stage === 'payment_pending' && signals.needsPayment) {
      const here =
        pathname === '/dashboard' || PAYMENT_ROUTES.some((r) => routeMatches(r, pathname));
      if (here) return { kind: 'start', key: 'kit' };
    }
  }
  return { kind: 'none' };
}
